import React, { useMemo } from 'react';
import {
  Button, Col, Form, Modal, Spinner,
} from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import { useAchievementsContext } from '../../../contexts/AchievementsContext/AchievementsContext';
import { useCharacterExpressionsContext } from '../../../contexts/CharacterExpressionsContext/CharacterExpressionsContext';
import { RequestState } from '../../../dorian-shared/hooks/useAsyncOperationState';
import { Branch } from '../../../dorian-shared/types/branch/Branch';
import { useEpisodeNodes } from '../../../services/EpisodeTextService/hooks/useEpisodeNodes';
import { MemoryDTO } from '../Book/MemoryBank/memoryBankTypes';
import { showToast } from '../../ui/utils';
import css from './ExportTextModal.module.scss';
import { stripIdSuffixes } from './utils/stripIdSuffixes';

type ExportTextModalProps = {
  episodeId: number;
  bookId?: number;
  preloadedNodes?: Branch[];
  memoryBank?: MemoryDTO[];
  onClose: () => void;
};

export function ExportTextModal(props: ExportTextModalProps) {
  const {
    episodeId,
    bookId,
    preloadedNodes,
    memoryBank,
    onClose,
  } = props;

  const possibleExpressions = useCharacterExpressionsContext();
  const { achievements } = useAchievementsContext();

  const {
    tsvText,
    requestState,
    errorMessage,
    isParsing,
  } = useEpisodeNodes(episodeId, {
    enabled: true,
    preloadedNodes,
    bookId,
    expressions: possibleExpressions,
    memoryBank,
    achievements,
  });

  const isLoading = requestState === RequestState.Loading || isParsing;
  const isSuccess = requestState === RequestState.Success;

  const exportedText = useMemo(
    () => (tsvText ? stripIdSuffixes(tsvText) : ''),
    [tsvText],
  );

  const csvData = exportedText
    ? exportedText.split('\n').map((row) => row.split('\t').map((cell) => cell.trim()))
    : [];

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exportedText ?? '')
      .then(() => {
        showToast({ textMessage: 'Text copied to clipboard!', variant: 'info' });
      })
      .catch(() => {
        showToast({ textMessage: 'Failed to copy text. Please select and copy manually.' });
      });
  };

  const isButtonDisabled = !exportedText || isLoading || !isSuccess;

  return (
    <>
      <Modal
        show
        size="xl"
        scrollable
        dialogClassName={css.modalDialog}
      >
        <Modal.Header>
          <Modal.Title>
            Export as Text
          </Modal.Title>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => window.open('https://home.dorian.live/dorianacademy/textimporter', '_blank')}
          >
            Documentation
          </Button>
        </Modal.Header>
        <Modal.Body>
          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              {errorMessage}
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>
              Text format for nodes and steps
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={20}
              value={exportedText}
              readOnly
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Col md="auto">
            {!isButtonDisabled && (
              <>
                <Button
                  variant="secondary"
                  className="mr-2 ml-3"
                  disabled={isButtonDisabled}
                  as={CSVLink}
                  separator={'\t'}
                  enclosingCharacter=""
                  data={csvData}
                  filename={`episode-${episodeId}-export.csv`}
                >
                  Download as CSV
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCopyToClipboard}
                  disabled={isButtonDisabled}
                >
                  Copy to Clipboard
                </Button>
              </>
            )}
            <Button
              onClick={onClose}
              className="mr-2 ml-5"
            >
              Close
            </Button>
          </Col>
        </Modal.Footer>
      </Modal>
      {isLoading && (
        <div className="text-center loadingSpinner" style={{ zIndex: 100000 }} data-testid="loading-spinner">
          <Spinner variant="primary" animation="border" role="status" />
          <div className="overlay" />
        </div>
      )}
    </>
  );
}
